import React, { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Download, FileCheck, Save, Upload } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { formatINR, formatINRCompact, formatDate } from "@/lib/format";
import MasterDataSelect from "@/components/MasterDataSelect";
import { useMasterList } from "@/hooks/useMasterData";
import { useAuth } from "@/context/AuthContext";
import ChangeHistory from "@/components/ChangeHistory";

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = (user?.roles || [user?.role]).includes("admin");

  const { data: deal, isLoading } = useQuery({
    queryKey: ["deal", id],
    queryFn: async () => (await api.get(`/deals/${id}`)).data,
  });

  const { data: dealStatuses = [] } = useMasterList("deal_status");

  const [form, setForm] = useState({});
  React.useEffect(() => {
    if (deal) setForm({
      final_bid_value: deal.final_bid_value ?? "",
      final_bid_value_unit_id: deal.final_bid_value_unit_id || "",
      brokerage_payable_by: deal.brokerage_payable_by || "",
      expected_closure_date: deal.expected_closure_date || "",
      actual_closure_date: deal.actual_closure_date || "",
      deal_status_id: deal.deal_status_id || "",
      invoice_number: deal.invoice_number || "",
      invoice_date: deal.invoice_date || "",
      payment_received_date: deal.payment_received_date || "",
    });
  }, [deal]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form };
      if (payload.final_bid_value === "" || payload.final_bid_value == null) delete payload.final_bid_value;
      else payload.final_bid_value = Number(payload.final_bid_value);
      for (const k of Object.keys(payload)) {
        if (payload[k] === "") delete payload[k];
      }
      return api.patch(`/deals/${id}`, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", id] });
      toast.success("Deal updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const upload = useMutation({
    mutationFn: async ({ kind, file }) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post(`/deals/${id}/files/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", id] });
      toast.success("File uploaded");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });

  const downloadFile = async (fileId, label) => {
    const res = await api.get(`/deals/${id}/files/${fileId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = label; a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading || !deal) {
    return <div className="p-8"><Skeleton className="h-32 w-full max-w-4xl" /></div>;
  }

  return (
    <div className="p-8 max-w-5xl pb-12">
      <button type="button" onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="deal-back">
        <ChevronLeft className="h-3 w-3" /> Back
      </button>
      <div className="mt-3">
        <div className="label-eyebrow">Deal</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid="deal-status-title">{deal.deal_status_label}</h1>
        <div className="text-sm text-slate-600 mt-1.5 flex gap-4 flex-wrap">
          <span>Match: <Link to="#" className="text-slate-900 font-semibold">{deal.match_id.slice(0, 8)}</Link></span>
          <span>Asset: <Link to={`/assets/${deal.asset_id}`} className="text-slate-900 font-semibold hover:underline">view</Link></span>
          <span>Lead: <Link to={`/leads/${deal.lead_id}`} className="text-slate-900 font-semibold hover:underline">view</Link></span>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="label-eyebrow">Final Bid (INR)</div>
          <div className="font-mono-num text-2xl font-bold text-slate-900 mt-1" data-testid="deal-final-bid">{deal.final_bid_value_inr ? formatINRCompact(deal.final_bid_value_inr) : "—"}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{deal.final_bid_value ?? "—"} ({deal.final_bid_value_unit_id ? "unit set" : "no unit"})</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="label-eyebrow">Brokerage</div>
          <div className="font-mono-num text-2xl font-bold text-emerald-700 mt-1" data-testid="deal-brokerage">{deal.brokerage_amount ? formatINR(deal.brokerage_amount) : "—"}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Auto from Lead's brokerage type/rate</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="label-eyebrow">Payable By</div>
          <div className="text-sm font-semibold text-slate-900 mt-1">{deal.brokerage_payable_by || "—"}</div>
        </div>
      </div>

      <div className="mt-5 bg-white border border-slate-200 rounded-md p-5">
        <div className="label-eyebrow">Edit Deal</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div>
            <Label className="text-xs">Final Bid Value</Label>
            <div className="flex gap-2 mt-1">
              <Input type="number" min="0" step="0.01" value={form.final_bid_value || ""} onChange={(e) => setForm({ ...form, final_bid_value: e.target.value })} data-testid="deal-bid-value" />
              <div className="w-28">
                <MasterDataSelect listType="price_unit" value={form.final_bid_value_unit_id || ""} onChange={(v) => setForm({ ...form, final_bid_value_unit_id: v })} placeholder="Unit" testId="deal-bid-unit" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Brokerage Payable By</Label>
            <Select value={form.brokerage_payable_by || ""} onValueChange={(v) => setForm({ ...form, brokerage_payable_by: v })}>
              <SelectTrigger className="mt-1" data-testid="deal-payable-by"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Buyer">Buyer</SelectItem>
                <SelectItem value="Seller-ARC-Bank">Seller-ARC-Bank</SelectItem>
                <SelectItem value="Both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Expected Closure Date</Label>
            <Input type="date" value={(form.expected_closure_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, expected_closure_date: e.target.value })} className="mt-1 font-mono-num" data-testid="deal-expected-closure" />
          </div>
          <div>
            <Label className="text-xs">Actual Closure Date</Label>
            <Input type="date" value={(form.actual_closure_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, actual_closure_date: e.target.value })} className="mt-1 font-mono-num" data-testid="deal-actual-closure" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Deal Status {!isAdmin && <span className="text-slate-400">(only admin can move beyond Won)</span>}</Label>
            <Select value={form.deal_status_id || ""} onValueChange={(v) => setForm({ ...form, deal_status_id: v })}>
              <SelectTrigger className="mt-1" data-testid="deal-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {dealStatuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <>
              <div>
                <Label className="text-xs">Invoice Number</Label>
                <Input value={form.invoice_number || ""} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="mt-1 font-mono-num" data-testid="deal-invoice-no" />
              </div>
              <div>
                <Label className="text-xs">Invoice Date</Label>
                <Input type="date" value={(form.invoice_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="mt-1 font-mono-num" data-testid="deal-invoice-date" />
              </div>
              <div>
                <Label className="text-xs">Payment Received Date</Label>
                <Input type="date" value={(form.payment_received_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, payment_received_date: e.target.value })} className="mt-1 font-mono-num" data-testid="deal-payment-date" />
              </div>
            </>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="deal-save">
            <Save className="h-4 w-4 mr-1.5" /> Save Deal
          </Button>
        </div>
      </div>

      <div className="mt-5 bg-white border border-slate-200 rounded-md p-5">
        <div className="label-eyebrow">Files</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          {[
            { key: "emd_payment_proof", file_id: deal.emd_payment_proof_file_id, label: "EMD Payment Proof" },
            { key: "result_confirmation", file_id: deal.result_confirmation_file_id, label: "Result Confirmation Document" },
          ].map((row) => (
            <div key={row.key} className="border border-slate-200 rounded p-3">
              <div className="flex items-center gap-2">
                <FileCheck className={`h-4 w-4 ${row.file_id ? "text-emerald-600" : "text-slate-300"}`} />
                <div className="text-sm font-semibold text-slate-900">{row.label}</div>
              </div>
              {row.file_id ? (
                <Button size="sm" variant="outline" className="mt-2" onClick={() => downloadFile(row.file_id, row.label)} data-testid={`deal-file-download-${row.key}`}>
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                </Button>
              ) : (
                <label className="mt-2 inline-flex">
                  <Button asChild size="sm" variant="outline">
                    <span><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload</span>
                  </Button>
                  <input
                    type="file"
                    className="hidden"
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xlsx"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) upload.mutate({ kind: row.key, file: f });
                      e.target.value = "";
                    }}
                    data-testid={`deal-file-upload-${row.key}`}
                  />
                </label>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <ChangeHistory entityType="Deal" entityId={id} />
      </div>
    </div>
  );
}
